import { todayPT } from "@homebound/activesupport";
import { Css, ModalProps } from "@homebound/beam";
import xor from "lodash/xor";
import { NavigateFunction } from "react-router-dom";
import { BidContractType, Maybe, SaveBidContractInput } from "src/generated/graphqlTypes";
import {
  BidPackageDetailActiveRequestFragment,
  BidPackageDetailPage_BidContractRevisionFragment,
  PageBidPackageDetailFragment,
} from "src/routes/bids/BidPackageDetailPage.generated";
import { INTERNAL_ESTIMATE_ID } from "src/routes/bids/plan-based-bid-package/utils";
import { AggregateMemberVersionChangeType } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignUpdatesAvailableButton";
import { createEnterPlanBasedBidUrl, createEnterUnitBasedBidUrl } from "src/RouteUrls";
import { pushInNextLoop } from "src/utils";
import { Temporal } from "temporal-polyfill";
import { PlanBasedBidsTableV2_BidPackageBidderFragment } from "../plan-based-bid-package/components/PlanBasedBidsTableV2.generated";
import { SelectBidderModal } from "../plan-based-bid-package/components/SelectBidderModal";
import { AddTradePartnerModal } from "./AddTradePartnerModal";

export function getBidPackageTradesWithEstimate(bp: {
  internalEstimateBidContract?: Maybe<{ id: string; archivedAt?: Maybe<Temporal.ZonedDateTime> }>;
  activeRequests: BidPackageDetailActiveRequestFragment[];
  latestVersion: { latestBidContractRevisions: BidPackageDetailPage_BidContractRevisionFragment[] };
}) {
  const estimate = bp.internalEstimateBidContract;
  return [
    ...(estimate && !estimate.archivedAt
      ? [
          {
            id: "hbr",
            tradePartner: { id: INTERNAL_ESTIMATE_ID, name: "Homebound Internal Estimate" },
            bidContract: estimate,
            bidContractRevision: bp.latestVersion.latestBidContractRevisions.find(
              (bcr) => bcr.bidContract.id === estimate?.id,
            ),
          },
        ]
      : []),
    ...bp.activeRequests.map((request) => ({
      ...request,
      bidContractRevision: bp.latestVersion.latestBidContractRevisions.find(
        (bcr) => bcr.bidContract.tradePartner?.id === request.tradePartner.id,
      ),
    })),
  ];
}

/** Expensive (# of JSON fields returned) cost codes that use a degraded PBBv1 scope-lines query (see P-2396). */
const HEAVY_SCOPE_COST_CODE_NUMBERS = new Set([
  "5001", // Pre-Fab Cabinets
]);
export function isHeavyScopeBidPackage(bidPackage: PageBidPackageDetailFragment) {
  return bidPackage.costCodes.some((costCode) => HEAVY_SCOPE_COST_CODE_NUMBERS.has(costCode.number));
}

/**
 * These fields determine if a scope line has changed.
 * This has been copied from the v1 component (PlanBasedBidsTable) and now shared with v2 (PlanBasedScopeTable)
 */
type ScopeDiffFields = {
  quantity?: Maybe<number>;
  location?: Maybe<{ id: string }>;
  unitOfMeasure?: Maybe<{ id: string }>;
  materialVariant?: Maybe<{ id: string }>;
  drop?: Maybe<{ id: string }>;
  options?: Maybe<readonly { id: string }[]>;
};
export type ScopeDiff = {
  changeType?: Maybe<string>;
  takeoffLineItemVersion: ScopeDiffFields;
  previous?: Maybe<ScopeDiffFields>;
};
export type ScopeDiffField = keyof ScopeDiffFields;

/**
 * Returns true if a field has been changed given the scope line diff
 */
export function scopeFieldHasChanged(diff: ScopeDiff, field?: ScopeDiffField): boolean {
  if (!diff.previous || !field) return false;
  if (field === "quantity") {
    return diff.previous.quantity !== diff.takeoffLineItemVersion.quantity;
  } else if (field === "options") {
    return optionsHaveChanged(diff);
  }
  const previous = diff.previous[field]?.id;
  const current = diff.takeoffLineItemVersion[field]?.id;
  return previous !== current;
}

/**
 * Apply a Css style to highlight a changed row given the diff field
 */
export function scopeDiffStyles(diff: ScopeDiff, field?: ScopeDiffField) {
  // We want to highlight the whole row if either the row change type is added or if the options on the row have changed
  if (diff.changeType === AggregateMemberVersionChangeType.ADDED || scopeFieldHasChanged(diff, "options")) {
    return Css.bgYellow50.$;
  }
  if (diff.changeType === AggregateMemberVersionChangeType.REMOVED) {
    return Css.bgGray50.tdlt.gray500.$;
  }
  if (diff.changeType === AggregateMemberVersionChangeType.UPDATED && scopeFieldHasChanged(diff, field)) {
    return Css.bgYellow50.$;
  }
}

function optionsHaveChanged(diff: ScopeDiff) {
  const prev = diff.previous;
  if (!prev || diff.changeType !== AggregateMemberVersionChangeType.UPDATED) return false;
  return xor(
    prev.options?.map((o) => o.id),
    diff.takeoffLineItemVersion.options?.map((o) => o.id),
  ).nonEmpty;
}

/**
 * Before going to the enter costs page, route based on how many bidders exist:
 * - 0 bidders: open the add-trade-partner modal (both trade partners and internal estimate can be added through this flow).
 * - 1 bidder: go straight to the enter-bid page.
 * - 2+ bidders: open the select-bidder modal so the user picks (trade partner or internal estimate).
 */
export async function goToEnterCostsPage(
  bidPackage: PageBidPackageDetailFragment,
  bidders: PlanBasedBidsTableV2_BidPackageBidderFragment[],
  openModal: (props: ModalProps) => void,
  navigate: NavigateFunction,
  saveBidContract?: (input: SaveBidContractInput) => void,
  productOfferingId?: string,
) {
  const goToEnterBid = async (bidderId: string) => {
    const bidder = bidders.find((b) => b.id === bidderId);
    if (!bidder) return;
    // If we don't have a bid contract, we need to create one
    if (!bidder.bidContract && saveBidContract) {
      // We don't need to worry about internal estimates here because they always should have a bid contract
      await saveBidContract({
        tradePartnerId: bidder.tradePartner?.id,
        // For an existing request, link the bid contract directly. For a new trade,
        // pass bidPackageId so it finds/creates the request.
        bidRequestId: bidder.request?.id,
        bidPackageId: bidder.request ? undefined : bidPackage.id,
        startDate: todayPT(),
        type: bidPackage.isUnitBased ? BidContractType.UnitBased : BidContractType.UserCreatedGroupBased,
      });
    }
    const url = bidPackage.isPlanBasedV2
      ? createEnterPlanBasedBidUrl(bidPackage.id, bidderId, { productOfferingId })
      : createEnterUnitBasedBidUrl(bidPackage.id, bidderId);
    pushInNextLoop(navigate, url);
  };

  if (bidders.isEmpty) {
    openModal({ content: <AddTradePartnerModal bidPackage={bidPackage} /> });
  } else if (bidders.length === 1) {
    await goToEnterBid(bidders[0].id);
  } else {
    openModal({ content: <SelectBidderModal bidders={bidders} onSelect={goToEnterBid} /> });
  }
}
